import { useMemo, useSyncExternalStore } from "react";

/** A clock ("1 second ago" → "2 minutes ago") that ticks every
 *  ``intervalMs`` without re-running the interval per subscriber: one
 *  timer per distinct interval, shared by every caller through
 *  ``useSyncExternalStore``, so a render never starts a timer itself.
 *  Paused while the tab is hidden (no point ticking a label nobody can
 *  see), and refreshed at once when it becomes visible again, so a
 *  backgrounded tab doesn't show a stale age the moment it's reopened. */

type Store = {
  nowMs: number;
  timer: ReturnType<typeof setInterval> | null;
  readonly subscribers: Set<() => void>;
};

const stores = new Map<number, Store>();

function getStore(intervalMs: number): Store {
  let store = stores.get(intervalMs);
  if (!store) {
    store = { nowMs: Date.now(), timer: null, subscribers: new Set() };
    stores.set(intervalMs, store);
  }
  return store;
}

function notify(store: Store): void {
  for (const listener of store.subscribers) listener();
}

function stopTimer(store: Store): void {
  if (store.timer !== null) {
    clearInterval(store.timer);
    store.timer = null;
  }
}

function startTimer(intervalMs: number, store: Store): void {
  if (store.timer !== null) return;
  if (typeof document !== "undefined" && document.hidden) return;
  store.nowMs = Date.now();
  store.timer = setInterval(() => {
    store.nowMs = Date.now();
    notify(store);
  }, intervalMs);
}

let visibilityBound = false;

function ensureVisibilityListener(): void {
  if (visibilityBound || typeof document === "undefined") return;
  visibilityBound = true;
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      for (const store of stores.values()) stopTimer(store);
      return;
    }
    for (const [intervalMs, store] of stores.entries()) {
      store.nowMs = Date.now();
      notify(store);
      if (store.subscribers.size > 0) startTimer(intervalMs, store);
    }
  });
}

/** The current time (ms), refreshed every ``intervalMs`` while this (or
 *  any other) subscriber is mounted. Good enough for relative-time
 *  labels ("2 minutes ago"); not a precise clock. */
export function useNow(intervalMs = 30_000): number {
  const store = getStore(intervalMs);
  const subscribe = useMemo(
    () => (listener: () => void): (() => void) => {
      ensureVisibilityListener();
      store.subscribers.add(listener);
      startTimer(intervalMs, store);
      return () => {
        store.subscribers.delete(listener);
        if (store.subscribers.size === 0) stopTimer(store);
      };
    },
    [intervalMs, store],
  );
  const getSnapshot = useMemo(() => () => store.nowMs, [store]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
